import React, { useState, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Grid,
  Button,
  Chip,
  Stack
} from '@mui/material';
import { motion } from 'framer-motion';

import BlogCard from '../../components/common/BlogCard';
import SEO from '../../components/SEO';
import useBlogData from '../../hooks/useBlogData';
import THEME from '../../constants/theme';

/**
 * BlogPage - Blog Listing Page
 * Features:
 * - Responsive grid: 1 column mobile, 2 tablet, 3 desktop
 * - Category filtering with chips (All Posts + each unique category)
 * - Load More functionality (show 3 initially, load 3 more per click)
 * - Card hover effects and animations
 * @returns {React.Component} BlogPage component
 */
const BlogPage = () => {
  const { blogs, loading } = useBlogData();
  const [selectedCategory, setSelectedCategory] = useState('All Posts');
  const [displayCount, setDisplayCount] = useState(4);

  // Extract unique categories
  const categories = useMemo(() => {
    const uniqueCategories = [
      'All Posts',
      ...new Set(blogs.flatMap(blog => blog.categories))
    ];
    return uniqueCategories;
  }, [blogs]);

  // Filter blogs by selected category
  const filteredBlogs = useMemo(() => {
    if (selectedCategory === 'All Posts') {
      return blogs;
    }
    return blogs.filter(blog => blog.categories.includes(selectedCategory));
  }, [blogs, selectedCategory]);

  // Slice blogs for load more functionality
  const displayedBlogs = filteredBlogs.slice(0, displayCount);
  const hasMoreBlogs = displayCount < filteredBlogs.length;

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 4);
  };

  const handleCategoryChange = category => {
    setSelectedCategory(category);
    setDisplayCount(4); // Reset to initial count when changing category
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <>
      <SEO
        title='Blog - MarQ Capital | Investment Insights and Market Analysis'
        description='Read the latest insights on quantitative trading, investment strategies, market analysis, and wealth management from MarQ Capital experts.'
        keywords='blog, investment insights, trading strategies, market analysis, wealth management, quantitative trading'
        canonical='/blog'
      />

      <Box
        sx={{
          minHeight: '100vh',
          py: { xs: 4, sm: 6, md: 8 },
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: THEME.COLORS.NEUTRAL_WHITE
        }}
      >
        <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 2 }}>
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ mb: { xs: 4, md: 8 }, textAlign: 'center' }}>
              <Typography
                variant='h1'
                sx={{
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  fontWeight: 800,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  mb: 2,
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
                }}
              >
                Investment Insights & Market Analysis
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  color: THEME.COLORS.NEUTRAL_BLACK_LIGHT,
                  maxWidth: '600px',
                  mx: 'auto',
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
                }}
              >
                Explore our latest articles on quantitative trading, investment
                strategies, and wealth building.
              </Typography>
            </Box>
          </motion.div>

          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Box
              sx={{
                mb: { xs: 3, md: 5 },
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Stack
                direction='row'
                spacing={1}
                sx={{
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: { xs: 1, sm: 1.5 }
                }}
              >
                {categories.map(category => (
                  <Chip
                    key={category}
                    label={category}
                    onClick={() => handleCategoryChange(category)}
                    sx={{
                      backgroundColor:
                        selectedCategory === category
                          ? THEME.COLORS.PRIMARY_BLUE
                          : 'rgba(30, 82, 243, 0.08)',
                      color:
                        selectedCategory === category
                          ? THEME.COLORS.NEUTRAL_WHITE
                          : THEME.COLORS.PRIMARY_BLUE,
                      border: `1px solid ${THEME.COLORS.PRIMARY_BLUE}`,
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      transition: `all ${THEME.TRANSITIONS.MEDIUM}`,
                      '&:hover': {
                        backgroundColor: THEME.COLORS.PRIMARY_BLUE,
                        color: THEME.COLORS.NEUTRAL_WHITE,
                        transform: 'translateY(-2px)'
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </motion.div>

          {/* Blog Cards Grid */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress sx={{ color: THEME.COLORS.PRIMARY_BLUE }} />
            </Box>
          ) : (
            <>
              <motion.div
                variants={containerVariants}
                initial='hidden'
                animate='visible'
              >
                <Grid
                  container
                  spacing={{ xs: 2, sm: 2.5, md: 3 }}
                  sx={{ mb: { xs: 4, md: 6 } }}
                >
                  {displayedBlogs.map(blog => (
                    <Grid item xs={12} sm={6} md={6} key={blog.id}>
                      <motion.div variants={cardVariants}>
                        <BlogCard blog={blog} />
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>

              {/* No posts message */}
              {filteredBlogs.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography
                      sx={{
                        fontSize: '1.1rem',
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
                      }}
                    >
                      No blog posts found for this category.
                    </Typography>
                  </Box>
                </motion.div>
              )}
            </>
          )}

          {/* Load More Button */}
          {hasMoreBlogs && filteredBlogs.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <Button
                onClick={handleLoadMore}
                variant='outlined'
                size='large'
                sx={{
                  color: 'var(--primary-blue)',
                  borderColor: 'var(--primary-blue)',
                  backgroundColor: 'rgba(30, 82, 243, 0.05)',
                  padding: { xs: '12px 32px', sm: '14px 48px' },
                  fontSize: { xs: '0.95rem', sm: '1rem' },
                  fontWeight: 600,
                  borderRadius: THEME.BORDERS.RADIUS.MEDIUM,
                  transition: `all ${THEME.TRANSITIONS.MEDIUM}`,
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                  '&:hover': {
                    backgroundColor: 'var(--primary-blue)',
                    color: 'var(--neutral-white)',
                    borderColor: 'var(--primary-blue)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 16px rgba(30, 82, 243, 0.2)`
                  }
                }}
              >
                Load More Articles
              </Button>
            </motion.div>
          )}

          {/* Results Count */}
          {filteredBlogs.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: '0.9rem',
                  color: 'rgba(255, 255, 255, 0.5)',
                  mt: 3,
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
                }}
              >
                Showing {displayedBlogs.length} of {filteredBlogs.length}{' '}
                articles
              </Typography>
            </motion.div>
          )}
        </Container>
      </Box>
    </>
  );
};

export default BlogPage;
